// preview.ts — the Look portal's faithful preview pipeline (#103, ADR-0028).
//
// The editor shows a Look exactly as the camera would render it. The camera works
// in RGB565; a browser image is 8-bit RGBA. So the pipeline is, in order:
//
//   sourceRGBA  --rgbaToRGB565-->  RGB565 frame  --applyDither (the Engine twin)-->
//   RGB565 (palette colors)  --rgb565ToRGBA-->  canvas-ready RGBA
//
// The leading RGBA→RGB565 reduction is the **mandatory front-stage** the parity audit
// flagged: feeding the twin raw 8-bit canvas pixels would diverge from the camera
// before the first quantize (the camera never sees more than 5/6/5 bits per channel).
// rgb565ToRGBA expands with the SAME 5→8 / 6→8 expansion the engine reads with
// (src/pixel.h: <<3, <<2), so what the canvas shows is byte-for-byte the camera's
// output — not an approximation.

import { applyDither, readRGB565Pixel, type Palette } from "./dither";

/** RGBA (8-bit) → the camera's RGB565 big-endian [hi,lo] frame (the front-stage). */
export function rgbaToRGB565(rgba: Uint8ClampedArray | Uint8Array, w: number, h: number, into?: Uint8Array): Uint8Array {
  const buf = into && into.length === w * h * 2 ? into : new Uint8Array(w * h * 2);  // reuse caller scratch (#250 OPT #4)
  for (let i = 0; i < w * h; i++) {
    const r = rgba[i * 4]!, g = rgba[i * 4 + 1]!, b = rgba[i * 4 + 2]!;
    const v = ((r >> 3) << 11) | ((g >> 2) << 5) | (b >> 3);
    buf[i * 2] = (v >> 8) & 0xff;
    buf[i * 2 + 1] = v & 0xff;
  }
  return buf;
}

/** RGB565 frame → opaque RGBA, expanding 5/6/5 exactly as the engine does (<<3,<<2,<<3). */
export function rgb565ToRGBA(buf: Uint8Array, w: number, h: number, into?: Uint8ClampedArray): Uint8ClampedArray {
  const out = into && into.length === w * h * 4 ? into : new Uint8ClampedArray(w * h * 4);  // reuse caller scratch (#250 OPT #4)
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const [r, g, b] = readRGB565Pixel(buf, w, x, y);
      const i = (y * w + x) * 4;
      out[i] = r; out[i + 1] = g; out[i + 2] = b; out[i + 3] = 255;
    }
  return out;
}

/** Build the engine twin's Palette from a Look's hex colors + adjustments. */
export function paletteFromHex(opts: {
  name?: string;
  colors: string[]; // "#rrggbb" or "rrggbb"
  mapMode: "nearest" | "luminance" | "gradient";
  brightness: number;
  contrast: number;
  saturation: number;
  // Per-Look tone ops (ADR-0033, #164 P4; optional, absent = identity). gamma is PERCENT (100 = identity).
  gamma?: number;
  negative?: boolean;
  solarize?: boolean;
  posterize?: number;
  hueRotate?: number;
  // Per-Look dither knobs (ADR-0033, #164 P5; optional, absent = identity). ditherStrength + glyphSize
  // are PERCENT (100 = native) — the twin does its own /100, so pass them straight through (no /100 here).
  ditherStrength?: number;
  ditherBias?: number;
  glyphSet?: string;
  glyphSize?: number;
  // Luma Key / Chroma Key / Splash (prototype, #235) — optional, absent = identity.
  lumaKey?: number;
  chromaKey?: number;
  splash?: number;
  vignette?: number;
  vignetteRadius?: number;
  vignetteFeather?: number;
}): Palette {
  const colors = opts.colors.map((hex): readonly [number, number, number] => {
    const n = hex.replace(/^#/, "");
    return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
  });
  return {
    name: opts.name ?? "",
    colors,
    colorCount: colors.length,
    mapMode: opts.mapMode,
    brightness: opts.brightness,
    contrast: opts.contrast,
    saturation: opts.saturation,
    gamma: opts.gamma,
    negative: opts.negative,
    solarize: opts.solarize,
    posterize: opts.posterize,
    hueRotate: opts.hueRotate,
    ditherStrength: opts.ditherStrength,
    ditherBias: opts.ditherBias,
    glyphSet: opts.glyphSet,
    glyphSize: opts.glyphSize,
    lumaKey: opts.lumaKey,
    chromaKey: opts.chromaKey,
    splash: opts.splash,
    vignette: opts.vignette,
    vignetteRadius: opts.vignetteRadius,
    vignetteFeather: opts.vignetteFeather,
  };
}

/**
 * Render a Look on a source image, returning canvas-ready RGBA that is byte-for-byte
 * what the camera produces: reduce to RGB565, run the Engine twin (style + palette),
 * expand back to RGBA. `w`×`h` is the View size (240×240 on the device, but any size
 * works for an editor preview).
 */
export function previewLook(
  sourceRGBA: Uint8ClampedArray | Uint8Array,
  w: number,
  h: number,
  style: number,
  palette: Palette,
  scratch?: { rgb565?: Uint8Array; rgba?: Uint8ClampedArray },  // reuse per-frame buffers (#250 OPT #4); output bytes identical
): Uint8ClampedArray {
  const buf = rgbaToRGB565(sourceRGBA, w, h, scratch?.rgb565);
  applyDither(buf, w, h, style, palette); // the twin — mutates buf in place
  return rgb565ToRGBA(buf, w, h, scratch?.rgba);
}
